
import React from 'react';
import { Button } from "@/components/ui/button";
import { Share2, MessageSquare, Instagram } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useNightModeStyles } from "@/hooks/useNightModeStyles";

const getEmojiTooltip = (emoji, layers) => {
  const tooltips = {
    1: {
      '👕': 't-shirt'
    },
    2: {
      '👕': 't-shirt',
      '🧥': 'light jacket'
    },
    3: {
      '👕': 't-shirt',
      '🧥': 'thick jacket',
      '🧣': 'scarf'
    },
    4: {
      '👕': 't-shirt',
      '🧥': 'winter jacket',
      '🧣': 'sweater & scarf',
      '👞': 'winter boots'
    },
    5: {
      '👕': 't-shirt',
      '🧥': 'winter jacket',
      '🧣': 'sweater & scarf',
      '🧤': 'winter gloves',
      '👞': 'winter boots'
    },
    weather: {
      '☔': 'umbrella or rain jacket'
    }
  };

  return tooltips[layers]?.[emoji] || tooltips.weather?.[emoji] || '';
};

// Function to detect if the user is on a mobile device
const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Regular expressions to check for mobile devices
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  
  return mobileRegex.test(userAgent);
};

const LayerRecommendation = ({ recommendation }) => {
  const { toast } = useToast();
  const { getNightModeClass } = useNightModeStyles();

  const shareText = `You will need ${recommendation.layers} layers ${recommendation.emojis.join('')}, ${recommendation.description}. https://layers.today`;

  const handleShare = async (platform) => {
    if (window.gtag) {
      window.gtag('event', 'share', {
        'event_category': 'Engagement',
        'event_label': platform,
        'value': recommendation.layers
      });
    }
    

    let url;
    switch (platform) {
      case 'whatsapp':
        // Different URLs for WhatsApp based on device type
        if (isMobileDevice()) {
          // Mobile devices use the whatsapp:// protocol
          url = `whatsapp://send?text=${encodeURIComponent(shareText)}`;
        } else {
          // Desktop devices use the web.whatsapp.com URL
          url = `https://web.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
        }
        break;
      case 'instagram':
        // Instagram sharing via DM URL scheme
        url = `instagram://share?text=${encodeURIComponent(shareText)}`;
        break;
      case 'imessage':
        url = `sms:&body=${encodeURIComponent(shareText)}`;
        break;
      default:
        await navigator.clipboard.writeText(shareText);
        toast({
          description: "Copied"
        });
        return;
    }

    try {
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to open share URL:', err);
      await navigator.clipboard.writeText(shareText);
      toast({
        description: "Copied to clipboard instead"
      });
    }
  };

  return (
    <div className={`flex items-center space-x-4 mb-3 p-4 rounded-lg ${recommendation.background} relative ${getNightModeClass()}`}>
      <div className="flex items-center space-x-2">
        <span className="text-6xl font-extralight text-foreground">{recommendation.layers}</span>
        <div className="flex space-x-1">
          <TooltipProvider delayDuration={0}>
            {recommendation.emojis.map((emoji, index) => (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <span className="text-4xl cursor-help touch-manipulation">
                    {emoji}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{getEmojiTooltip(emoji, recommendation.layers)}</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </TooltipProvider>
        </div>
      </div>
      <span className="text-body-lg text-foreground">
        {recommendation.description}
      </span>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon"
            className="absolute bottom-2 right-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => handleShare('whatsapp')}>
            <MessageSquare className="h-4 w-4 mr-2" />
            WhatsApp
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleShare('imessage')}>
            <MessageSquare className="h-4 w-4 mr-2" />
            iMessage
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleShare('instagram')}>
            <Instagram className="h-4 w-4 mr-2" />
            Instagram
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleShare('copy')}>
            <Share2 className="h-4 w-4 mr-2" />
            Copy Link
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default LayerRecommendation;
